import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, TablePagination, CircularProgress, TextField, Grid, Autocomplete, Chip } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Upload as UploadIcon } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
// Local lightweight debounce to avoid extra typings
const debounceFn = (fn: (...args: any[]) => void, wait = 300) => {
  let t: any
  const debounced = (...args: any[]) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
  debounced.cancel = () => clearTimeout(t)
  return debounced as any
}

import { misService } from '../services/misService'
import clientStoreService from '../services/clientStoreService'
import MISForm from '../components/mis/MISForm'

const MISManagement: React.FC = () => {
  const [rows, setRows] = useState<any[]>([])
  const [openForm, setOpenForm] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [detailRow, setDetailRow] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [downloadingSdWallet, setDownloadingSdWallet] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportFromDate, setExportFromDate] = useState<Date | null>(null)
  const [exportToDate, setExportToDate] = useState<Date | null>(null)
  const [exportClientName, setExportClientName] = useState<string | null>(null)
  const [page, setPage] = useState<number>(0) // zero-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState<number>(20) // default 20 per request
  const [total, setTotal] = useState<number>(0)
  const navigate = useNavigate()
  const HIDDEN_COLS = ['id','month','year','Hub','createdAt', 'DamagePending', 'TrafficPending', 'EvRentPending'];

  // Format Date object to YYYY-MM-DD for backend
  const formatYMD = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
  }

  // Render status as colored chips for better visibility
  const getStatusColor = (status?: string) => {
    switch ((status || '').toString().toUpperCase()) {
      case 'PENDING':
        return 'warning'
      case 'PAID':
        return 'success'
      case 'DEDUCTED':
        return 'error'
      case 'HOLD':
        return 'info'
      default:
        return 'default'
    }
  }

  // Format date and time in Indian format
  const formatIndianDateTime = (val: string | Date) => {
    try {
      const date = new Date(val);
      // Indian format: DD/MM/YYYY HH:mm:ss
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      };
      return date.toLocaleString('en-IN', options);
    } catch {
      return String(val);
    }
  };

  // Format date only in Indian format
  const formatIndianDate = (val: string | Date) => {
    try {
      const date = new Date(val);
      // Indian format: DD/MM/YYYY
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return String(val);
    }
  };

  const getApiErrorMessage = (err: any, fallback = 'Failed to load data') => {
    if (!err) return fallback;
    const responseData = err?.response?.data;
    if (responseData) {
      if (typeof responseData === 'string') return responseData;
      return responseData.message || responseData.error || fallback;
    }
    return err.message || fallback;
  };

  // Download handler for export dialog
  const handleDownloadFromDialog = async () => {
    if (!exportFromDate || !exportToDate) {
      setError('Please select From and To dates for export');
      return;
    }

    try {
      setExporting(true);
      setError(null);

      const params: any = {
        fromDate: formatYMD(exportFromDate as Date),
        toDate: formatYMD(exportToDate as Date),
        format: 'excel',
      };
      if (exportClientName) params.clientName = exportClientName;

      const res = await misService.exportMIS(params);

      // Create blob and download
      const contentType = res.headers['content-type'];
      const blob = new Blob([res.data], { type: typeof contentType === 'string' ? contentType : 'application/octet-stream' });

      // Try to get filename from content-disposition header
      let filename = `MIS_${Date.now()}.xlsx`;
      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      if (typeof cd === 'string' && cd) {
        const match = /filename\*=UTF-8''([^;\n\r]+)/.exec(cd) || /filename="?([^";]+)"?/.exec(cd);
        if (match) filename = decodeURIComponent(match[1]);
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Close dialog on success
      setExportDialogOpen(false);
    } catch (err: any) {
      console.error('Export failed', err?.response?.data || err);
      setError(getApiErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadSdWallet = async () => {
    try {
      setDownloadingSdWallet(true);
      setError(null);

      const res = await misService.exportSdWallet();
      const contentType = res.headers['content-type'];
      const blob = new Blob([res.data], { type: typeof contentType === 'string' ? contentType : 'application/octet-stream' });

      let filename = `RiderSdWallet_${Date.now()}.xlsx`;
      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      if (typeof cd === 'string' && cd) {
        const match = /filename\*=UTF-8''([^;\n\r]+)/.exec(cd) || /filename="?([^";]+)"?/.exec(cd);
        if (match) filename = decodeURIComponent(match[1]);
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('SD wallet export failed', err?.response?.data || err);
      setError(getApiErrorMessage(err, 'Failed to export SD wallet'));
    } finally {
      setDownloadingSdWallet(false);
    }
  };

  const requestIdRef = useRef(0)

  const [filters, setFilters] = useState<any>({ riderPublicRiderId: '', clientName: '', City: '', fromDate: null, toDate: null })
  const [appliedFilters, setAppliedFilters] = useState<any>({})
  const [clients, setClients] = useState<any[]>([])
  const [cities, setCities] = useState<any[]>([])
  const [clientInput, setClientInput] = useState<string>('')
  const [cityInput, setCityInput] = useState<string>('')
  const [loadingClients, setLoadingClients] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)

  const load = async (pageParam = page, limitParam = rowsPerPage, filterParams = appliedFilters) => {
    const reqId = ++requestIdRef.current

    try {
      setLoading(true)
      setError(null)

      // Prepare params: convert dates to ISO strings if present
      const params: any = { page: pageParam + 1, limit: limitParam }
      if (filterParams?.riderPublicRiderId) params.riderPublicRiderId = filterParams.riderPublicRiderId
      if (filterParams?.clientName) params.clientName = filterParams.clientName
      if (filterParams?.City) params.City = filterParams.City
      if (filterParams?.fromDate) params.fromDate = formatYMD(filterParams.fromDate as Date)
      if (filterParams?.toDate) params.toDate = formatYMD(filterParams.toDate as Date)

      const res = await misService.getMIS(params)

      // if a newer request started, ignore this response
      if (requestIdRef.current !== reqId) return
      setRows(res.data || [])
      setTotal(res.meta?.total ?? 0)
    } catch (err: any) {
      if (requestIdRef.current !== reqId) return
      console.error('Failed loading MIS rows', err?.response?.data || err)
      setError(getApiErrorMessage(err, 'Failed to load data'))
      setRows([])
      setTotal(0)
    } finally {
      if (requestIdRef.current === reqId) setLoading(false)
    }
  }

  // Re-load whenever page, pageSize or applied filters change
  useEffect(() => { load(page, rowsPerPage, appliedFilters) }, [page, rowsPerPage, appliedFilters])

  const handleCreate = () => { setEditing(null); setOpenForm(true) }
  const handleEdit = (row: any) => { setEditing(row); setOpenForm(true) }
  const handleBulk = () => { navigate('/mis/bulk-upload') }
  const handleVehicleHistory = () => { navigate('/rider-management/vehicle-history') }

  const handleSaved = () => { setOpenForm(false); load() }

  // Filter handlers
  const applyFiltersDebounced = React.useCallback(() => {
    setPage(0)
    setAppliedFilters({ ...filters })
  }, [filters])

  const debouncedApply = useRef<ReturnType<typeof debounceFn>>(debounceFn(applyFiltersDebounced, 400))

  useEffect(() => {
    debouncedApply.current = debounceFn(applyFiltersDebounced, 400)
    return () => {
      debouncedApply.current.cancel()
    }
  }, [applyFiltersDebounced])

  const applyFilters = () => {
    debouncedApply.current.cancel()
    setPage(0)
    setAppliedFilters(filters)
  }

  const clearFilters = () => {
    debouncedApply.current.cancel()
    setFilters({ riderPublicRiderId: '', clientName: '', City: '', fromDate: null, toDate: null })
    setClientInput('')
    setCityInput('')
    setAppliedFilters({})
    setPage(0)
  }

  // If you'd rather auto-apply as user types, trigger debouncedApply.current() in onChange of inputs

  // Load clients and cities for dropdowns
  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoadingClients(true)
        const res = await clientStoreService.getClients({ limit: 1000 })
        if (res.success && Array.isArray(res.data)) {
          // Deduplicate clients by name
          const seen = new Set<string>()
          const uniqueClients = res.data.filter((client) => {
            const key = client.name || client.clientCode || client.id
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })
          setClients(uniqueClients)
        }
      } catch (err) {
        console.warn('Failed loading clients', err)
      } finally {
        setLoadingClients(false)
      }
    }

    const loadCities = async () => {
      try {
        setLoadingCities(true)
        const res = await clientStoreService.getCities()
        if (res.success && Array.isArray(res.data)) setCities(res.data.map((c: any) => c.name || (c as any).name))
      } catch (err) {
        console.warn('Failed loading cities', err)
      } finally {
        setLoadingCities(false)
      }
    }

    loadClients()
    loadCities()
  }, [])

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: '0 1 auto' }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>MIS Management</Typography>
          <Typography variant="body2" color="textSecondary">List, create, edit MIS rows and bulk upload</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' }, width: { xs: '100%', md: 'auto' }, minWidth: 0, flex: '1 1 auto' }}>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={handleBulk} sx={{ width: { xs: '100%', sm: 'auto' } }}>Upload</Button>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={handleVehicleHistory} sx={{ width: { xs: '100%', sm: 'auto' } }}>Vehicle History</Button>
          <Button variant="outlined" onClick={() => navigate('/mis/history-summary')} sx={{ width: { xs: '100%', sm: 'auto' } }}>History Summary</Button>
          <Button variant="outlined" onClick={() => { setExportDialogOpen(true); setExportFromDate(filters.fromDate || null); setExportToDate(filters.toDate || null); setExportClientName(filters.clientName || null); }} sx={{ width: { xs: '100%', sm: 'auto' } }}>Download Payout File</Button>
          <Button variant="outlined" onClick={handleDownloadSdWallet} disabled={downloadingSdWallet} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {downloadingSdWallet ? 'Downloading SD Wallet...' : 'Download SD Wallet'}
          </Button>
          <Button variant="contained" color="primary" onClick={handleCreate} sx={{ width: { xs: '100%', sm: 'auto' } }}>Create MIS</Button>
        </Box>
      </Box>

      <Paper style={{ padding: 12, marginBottom: 12 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="Rider Public ID"
              value={filters.riderPublicRiderId}
              onChange={(e) => {
                setFilters({ ...filters, riderPublicRiderId: e.target.value })
                debouncedApply.current?.()
              }}
              fullWidth
              placeholder="Search by rider public id"
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters() }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              options={clients}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.name || opt.clientCode || opt.id || '')}
              value={clients.find(c => c.name === filters.clientName) || (filters.clientName ? { name: filters.clientName } : null)}
              onChange={(_e, v) => {
                const val = v ? (typeof v === 'string' ? v : v.name) : ''
                setFilters({ ...filters, clientName: val })
                setClientInput(val)
              }}
              inputValue={clientInput}
              onInputChange={(_e, val) => {
                setClientInput(val)
                setFilters({ ...filters, clientName: val })
              }}
              freeSolo
              loading={loadingClients}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Select or type client" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Autocomplete
              options={cities}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.name || '')}
              value={filters.City || null}
              onChange={(_e, v) => {
                const val = v || ''
                setFilters({ ...filters, City: val })
                setCityInput(val)
              }}
              inputValue={cityInput}
              onInputChange={(_e, val) => {
                setCityInput(val)
                setFilters({ ...filters, City: val })
              }}
              freeSolo
              loading={loadingCities}
              renderInput={(params) => (
                <TextField {...params} label="City" placeholder="Select or type city" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From"
                value={filters.fromDate}
                onChange={(d) => setFilters({ ...filters, fromDate: d })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="To"
                value={filters.toDate}
                onChange={(d) => setFilters({ ...filters, toDate: d })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={2} style={{ textAlign: 'right' }}>
            <Button variant="contained" color="primary" onClick={applyFilters} style={{ marginRight: 8 }}>Apply</Button>
            <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>Clear</Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '6px 10px' } }}>
            <TableHead>
              <TableRow>
                {/* Build headers dynamically from first row, excluding id */}
                {rows.length > 0 ? (
                  Object.keys(rows[0]).filter(k => !HIDDEN_COLS.includes(k)).map((key) => (
                    <TableCell key={key} align={typeof rows[0][key] === 'number' ? 'right' : 'left'}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                    </TableCell>
                  ))
                ) : (
                  <>
                    <TableCell>Rider</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Deduction</TableCell>
                    <TableCell align="right">Final Payout</TableCell>
                    <TableCell align="right">Pending</TableCell>
                  </>
                )}

                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={999} style={{ textAlign: 'center', padding: 20 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={999} style={{ textAlign: 'center', padding: 20 }}>
                    {error ? <Typography color="error">{error}</Typography> : <Typography color="textSecondary">No rows</Typography>}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r, idx) => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer', minHeight: 36, backgroundColor: idx % 2 === 0 ? 'action.hover' : 'background.paper' }} onClick={() => setDetailRow(r)}>
                    {/* Render cells dynamically in same order */}
                    {Object.keys(rows[0]).filter(k => !HIDDEN_COLS.includes(k)).map((col) => {
                      const val = (r as any)[col];

                      // Date detection (ISO date strings)
                      const isDate = typeof val === 'string' && /\d{4}-\d{2}-\d{2}T/.test(val);
                      const isDateShort = typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val);

                      let display = val == null || val === '' ? '-' : (
                        isDate || isDateShort ? (
                          // Format updatedAt with date and time, others just date
                          col.toLowerCase() === 'updatedat' ? formatIndianDateTime(val) : formatIndianDate(val)
                        ) : (typeof val === 'number' ? val.toLocaleString() : String(val))
                      );

                      const align = typeof val === 'number' ? 'right' : 'left';

                      // Special handling for `status` column: render colored chip
                      if (col.toLowerCase() === 'status') {
                        return (
                          <TableCell key={col} align={align as any} style={{ padding: '6px 10px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {display === '-' ? '-' : (
                              <Chip label={String(display)} color={getStatusColor(String(display)) as any} size="small" variant="outlined" />
                            )}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={col} align={align as any} style={{ padding: '6px 10px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {display}
                        </TableCell>
                      );
                    })}

                    <TableCell style={{ padding: '6px 10px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(() => {
                          const rowStatus = String(r.status || '').toUpperCase()
                          const isPaid = rowStatus === 'PAID'
                          return (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isPaid}
                              onClick={(e) => { e.stopPropagation(); if (!isPaid) handleEdit(r) }}
                            >
                              {isPaid ? 'Paid' : 'Edit'}
                            </Button>
                          )
                        })()}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { const v = parseInt(e.target.value, 10); setRowsPerPage(v); setPage(0); }}
          rowsPerPageOptions={[15, 20, 30, 50]}
          labelRowsPerPage="Rows per page"
        />
      </Paper>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit MIS' : 'Create MIS'}</DialogTitle>
        <DialogContent>
          <MISForm initialData={editing} onSaved={handleSaved} onCancel={() => setOpenForm(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Download Payout File</DialogTitle>
        <DialogContent>
          <Box mt={1} mb={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From"
                value={exportFromDate}
                onChange={(d) => setExportFromDate(d)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>

          <Box mb={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="To"
                value={exportToDate}
                onChange={(d) => setExportToDate(d)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>

          <Box mb={2}>
            <Autocomplete
              options={clients}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.name || opt.clientCode || opt.id || '')}
              value={clients.find(c => c.name === exportClientName) || (exportClientName ? { name: exportClientName } : null)}
              onChange={(_e, v) => setExportClientName(v ? (typeof v === 'string' ? v : v.name) : null)}
              freeSolo
              renderInput={(params) => (
                <TextField {...params} label="Client (optional)" placeholder="Select or type client" fullWidth />
              )}
            />
          </Box>

          {error ? <Typography color="error">{error}</Typography> : null}
        </DialogContent>

        <Box display="flex" justifyContent="flex-end" p={2}>
          <Button onClick={() => setExportDialogOpen(false)} style={{ marginRight: 8 }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleDownloadFromDialog} disabled={!exportFromDate || !exportToDate || exporting}>
            {exporting ? 'Downloading...' : 'Download'}
          </Button>
        </Box>
      </Dialog>

      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>MIS - Pending Breakdown</DialogTitle>
        <DialogContent>
          {detailRow ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, borderRadius: 2, backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Pending Amounts
                  </Typography>
                  <Box sx={{ mt: 1, display: 'grid', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, backgroundColor: 'background.paper', borderRadius: 1 }}>
                      <Typography color="textSecondary">Damage Pending</Typography>
                      <Typography variant="h6">{detailRow.DamagePending != null ? Number(detailRow.DamagePending).toLocaleString('en-IN') : '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, backgroundColor: 'background.paper', borderRadius: 1 }}>
                      <Typography color="textSecondary">Traffic Pending</Typography>
                      <Typography variant="h6">{detailRow.TrafficPending != null ? Number(detailRow.TrafficPending).toLocaleString('en-IN') : '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, backgroundColor: 'background.paper', borderRadius: 1 }}>
                      <Typography color="textSecondary">Ev Rent Pending</Typography>
                      <Typography variant="h6">{detailRow.EvRentPending != null ? Number(detailRow.EvRentPending).toLocaleString('en-IN') : '-'}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, borderRadius: 2, backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Row Details
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
                    <Typography variant="body2"><strong>Rider:</strong> {detailRow.RiderName || detailRow.riderPublicRiderId || '-'}</Typography>
                    <Typography variant="body2"><strong>Client:</strong> {detailRow.clientName || detailRow.Client || '-'}</Typography>
                    <Typography variant="body2"><strong>Period:</strong> {detailRow.periodStartDate ? new Date(detailRow.periodStartDate).toLocaleDateString('en-IN') : '-'} - {detailRow.periodEndDate ? new Date(detailRow.periodEndDate).toLocaleDateString('en-IN') : '-'}</Typography>
                    <Typography variant="body2"><strong>Status:</strong> {detailRow.status || '-'}</Typography>
                    <Typography variant="body2"><strong>Total Pending:</strong> {((detailRow.pending || detailRow.pending || detailRow.pending) ? Number(detailRow.pending || detailRow.pending || detailRow.pending).toLocaleString('en-IN') : '-')}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" p={2}>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </Box>
      </Dialog>

    </Box>
  )
}

export default MISManagement
